/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { searchPairRequestAction } from '@root/entities/pair'
import { pairRegistry } from '@root/entities/pair/atoms'
import { PairAddress, PairRef } from '@root/entities/pair/model'
import { PlatformUtil } from '@root/shared'
import { reatomResource } from '@root/shared/lib'

const clipboardAddressAtom = atom<PairRef.Ref | undefined>(
  undefined,
  'clipboardAddressAtom',
)

const parseAddress = (text: string): PairRef.Ref | undefined => {
  if (Network.validateAddress(Network.Solana)(text)) {
    return { network: Network.Solana, address: PairAddress(text) }
  }
  // No support for other networks yet
  return undefined
}

export const clipboardPairStateAtom = reatomResource(
  async (ctx) => {
    const clipboardData = ctx.spy(clipboardAddressAtom)
    if (!clipboardData) {
      return Result.succeed(undefined)
    }

    if (clipboardData.network !== Network.Solana) {
      // No support for other networks yet
      return Result.succeed(undefined)
    }

    const result = await ctx.schedule(() =>
      searchPairRequestAction(ctx, {
        query: clipboardData.address,
        network: clipboardData.network,
      }),
    )

    return result.pipe(
      Result.map((it) => {
        const pairDto = it.at(0)
        if (!pairDto) {
          return undefined
        }
        return pairRegistry.resolveFromDto(ctx, pairDto)
      }),
    )
  },
  { name: 'clipboardPairAtom' },
)
export const clipboardPairAtom = atom((ctx) => {
  return ctx.spy(clipboardPairStateAtom).value
})

const syncClipboardAction = action(async (ctx) => {
  const result = await ctx.schedule(() => PlatformUtil.readFromClipboard())
  if (Result.isFailure(result)) {
    return
  }

  const text = result.success
  if (!text) {
    clipboardAddressAtom(ctx, undefined)
    return
  }

  const trimmed = text.trim()
  if (!trimmed) {
    clipboardAddressAtom(ctx, undefined)
    return
  }
  const parsed = parseAddress(trimmed)

  if (!parsed) {
    clipboardAddressAtom(ctx, undefined)
    return
  }

  const current = ctx.get(clipboardAddressAtom)
  if (current && PairRef.eq(current, parsed)) {
    return
  }

  clipboardAddressAtom(ctx, parsed)
}, 'syncClipboardAction')

export const initializeClipboardPairAction = action((ctx) => {
  /*
   * The desktop build only.
   *
   * Reading the clipboard without being asked is free in Tauri and costs a
   * permission prompt in a browser — so this put "staging-app.kol.trade wants
   * to see text and images you copied" in front of every new visitor, before
   * they had clicked anything and while the sign-up screen was still behind
   * it. Nothing a person has not asked for is worth that.
   *
   * The reading the app does on a real gesture — pasting an OTP, importing a
   * list of wallets — is unaffected: a browser grants those without asking,
   * because the person is the one who acted.
   */
  if (!PlatformUtil.is('tauri')) {
    return
  }

  syncClipboardAction(ctx)

  window.addEventListener('focus', () => {
    syncClipboardAction(ctx)
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      syncClipboardAction(ctx)
    }
  })
  document.addEventListener('copy', () => {
    syncClipboardAction(ctx)
  })
  document.addEventListener('cut', () => {
    syncClipboardAction(ctx)
  })
  document.addEventListener('app:copy', () => {
    syncClipboardAction(ctx)
  })
}, 'initializeClipboardPairAction')
